import React, { useEffect, useState } from 'react';
import '@wangeditor/editor/dist/css/style.css';
import { Editor, Toolbar } from '@wangeditor/editor-for-react';
import { message } from 'antd';
import { API_BASE_URL } from '../utils/api';

const WangEditor = ({
  value = '',
  onChange,
  placeholder = '请输入内容',
}) => {
  const [editor, setEditor] = useState(null);
  const [html, setHtml] = useState(value || '');

  // 上传进度状态
  const [uploadProgress, setUploadProgress] = useState(null);

  // 编辑器配置
  const editorConfig = {
    placeholder,
    MENU_CONF: {
      uploadImage: {
        async customUpload(file, insertFn) {
          // 检查文件大小，限制为 10MB (与后端一致)
          const maxImageSize = 10 * 1024 * 1024; // 10MB
          if (file.size > maxImageSize) {
            const sizeMB = (file.size / 1024 / 1024).toFixed(2);
            message.error(`图片文件过大（${sizeMB}MB），请上传小于 10MB 的图片`);
            return;
          }

          const formData = new FormData();
          formData.append('file', file);

          try {
            setUploadProgress({ fileName: file.name, percent: 0 });

            const response = await new Promise((resolve, reject) => {
              const xhr = new XMLHttpRequest();

              xhr.upload.addEventListener('progress', (e) => {
                if (e.lengthComputable) {
                  const percent = Math.round((e.loaded / e.total) * 100);
                  setUploadProgress({ fileName: file.name, percent });
                }
              });

              xhr.addEventListener('load', () => {
                resolve({
                  ok: xhr.status >= 200 && xhr.status < 300,
                  status: xhr.status,
                  json: () => JSON.parse(xhr.responseText),
                  text: () => xhr.responseText
                });
              });

              xhr.addEventListener('error', () => {
                reject(new Error('图片上传失败，请检查网络连接'));
              });

              xhr.addEventListener('abort', () => {
                reject(new Error('上传已取消'));
              });

              xhr.open('POST', `${API_BASE_URL}/blog/editor/uploadimage`);
              xhr.send(formData);
            });

            // 检查响应状态
            if (!response.ok) {
              const errorText = await response.text();
              console.error('图片上传失败，状态码:', response.status, '错误信息:', errorText);
              setUploadProgress(null);
              message.error(`图片上传失败: ${response.status === 413 ? '文件过大，请选择小于 10MB 的图片' : '服务器错误，请稍后重试'}`);
              return;
            }

            const result = await response.json();

            if (result.state === 'SUCCESS') {
              setUploadProgress(null);
              insertFn(result.url);
            } else {
              setUploadProgress(null);
              message.error(result.message || '图片上传失败，请稍后重试');
            }
          } catch (error) {
            console.error('图片上传失败:', error);
            setUploadProgress(null);
            message.error('图片上传失败，请检查网络连接后重试');
          }
        },
      },
      uploadVideo: {
        async customUpload(file, insertFn) {
          // 检查文件大小，限制为 200MB (与后端一致)
          const maxVideoSize = 200 * 1024 * 1024; // 200MB
          if (file.size > maxVideoSize) {
            const sizeMB = (file.size / 1024 / 1024).toFixed(2);
            message.error(`视频文件过大（${sizeMB}MB），请上传小于 200MB 的视频`);
            return;
          }

          const formData = new FormData();
          formData.append('file', file);

          try {
            setUploadProgress({ fileName: file.name, percent: 0 });

            const response = await new Promise((resolve, reject) => {
              const xhr = new XMLHttpRequest();

              xhr.upload.addEventListener('progress', (e) => {
                if (e.lengthComputable) {
                  const percent = Math.round((e.loaded / e.total) * 100);
                  setUploadProgress({ fileName: file.name, percent });
                }
              });

              xhr.addEventListener('load', () => {
                resolve({
                  ok: xhr.status >= 200 && xhr.status < 300,
                  status: xhr.status,
                  json: () => JSON.parse(xhr.responseText),
                  text: () => xhr.responseText
                });
              });

              xhr.addEventListener('error', () => {
                reject(new Error('图片上传失败，请检查网络连接'));
              });

              xhr.addEventListener('abort', () => {
                reject(new Error('视频上传已取消'));
              });

              xhr.addEventListener('timeout', () => {
                reject(new Error('视频上传超时'));
              });

              xhr.open('POST', `${API_BASE_URL}/blog/editor/uploadvideo`);
              xhr.timeout = 300000; // 5 分钟超时
              xhr.send(formData);
            });

            // 检查响应状态
            if (!response.ok) {
              const errorText = await response.text();
              console.error('视频上传失败，状态码:', response.status, '错误信息:', errorText);
              setUploadProgress(null);

              if (response.status === 413) {
                message.error('视频文件过大，请上传小于 200MB 的视频');
              } else if (response.status === 408) {
                message.error('视频上传超时，请检查网络连接或尝试上传较小的文件');
              } else {
                message.error(`视频上传失败: ${response.status}，请稍后重试`);
              }
              return;
            }

            const result = await response.json();

            if (result.state === 'SUCCESS') {
              setUploadProgress(null);
              insertFn(result.url);
            } else {
              setUploadProgress(null);
              message.error(result.message || '视频上传失败，请稍后重试');
            }
          } catch (error) {
            console.error('视频上传失败:', error);
            setUploadProgress(null);
            if (error.message === '视频上传超时') {
              message.error('视频上传超时，请检查网络连接或尝试上传较小的文件');
            } else {
              message.error('视频上传失败，请检查网络连接后重试');
            }
          }
        },
      },
    },
  };

  const toolbarConfig = {};

  // 及时销毁 editor
  useEffect(() => {
    return () => {
      if (editor == null) return;
      editor.destroy();
      setEditor(null);
    };
  }, [editor]);

  const handleCreated = (editor) => {
    setEditor(editor);
    if (value) {
      editor.setHtml(value);
    }
  };

  const handleChange = (editor) => {
    const newHtml = editor.getHtml();
    setHtml(newHtml);
    if (onChange) {
      onChange(newHtml);
    }
  };

  return (
    <div>
      {/* 上传进度显示 */}
      {uploadProgress && (
        <div
          style={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            backgroundColor: 'rgba(0, 0, 0, 0.75)',
            color: 'white',
            padding: '20px 30px',
            borderRadius: '8px',
            zIndex: 1000,
            textAlign: 'center',
          }}
        >
          <div style={{ marginBottom: '10px', fontSize: '16px' }}>
            正在上传: {uploadProgress.fileName}
          </div>
          <div style={{ width: '300px', marginBottom: '10px' }}>
            <div
              style={{
                height: '8px',
                backgroundColor: '#e8e8e8',
                borderRadius: '4px',
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  height: '100%',
                  width: `${uploadProgress.percent}%`,
                  backgroundColor: '#1890ff',
                  transition: 'width 0.3s ease',
                }}
              />
            </div>
          </div>
          <div style={{ fontSize: '14px', color: '#d9d9d9' }}>
            {uploadProgress.percent}%
          </div>
        </div>
      )}
      <div style={{ border: '1px solid #ccc', zIndex: 100 }}>
        <Toolbar
          editor={editor}
          defaultConfig={toolbarConfig}
          mode="default"
          style={{ borderBottom: '1px solid #ccc', backgroundColor: '#f1f1f1' }}
        />
        <Editor
          defaultConfig={editorConfig}
          value={html}
          onCreated={handleCreated}
          onChange={handleChange}
          mode="default"
          style={{ height: '400px', overflowY: 'hidden' }}
        />
      </div>
    </div>
  );
};

export default WangEditor;
